/**
 * SDK 全局配置
 *
 * 默认值:
 * - basePath: 从 hotel-hub.js 脚本 URL 推导(/hub/hotel-hub.js -> /hub),可覆盖
 * - domain: 默认从 location.host 推导 root 域;可覆盖(本地/测试模拟真实域名)
 *
 * 不注入 build-time env:SDK 脱离 Next 运行时,env 在浏览器不可用;
 * 业务请求走同源 BFF（/hub/api/*），上游 whapi 由 hotel-hub 服务端 env 决定，SDK 不配置上游地址。
 *
 * 预配置全局:在 hotel-hub.js 加载(<script defer>)前,集成方可设置
 *   window.__HOTEL_SDK_CONFIG__ = { domain?, basePath? }
 * SDK 模块初始化时读取并合并,早于 Web Component 挂载--适合需要影响 hotel/info 首次查询的配置。
 */
import type { SDKConfig } from './types';

const PREINIT_GLOBAL = '__HOTEL_SDK_CONFIG__';

/** 已解析配置(内部使用,字段确定存在) */
export interface ResolvedConfig {
  basePath: string;
  domain?: string;
  /** 是否自动挂载客服挂件(默认 true) */
  customerService: boolean;
  /** 是否开启业务埋点上报(默认 true) */
  analytics: boolean;
  /** 是否开启调试日志(默认 false) */
  debug: boolean;
}

/**
 * 读取 hotel-hub.js 加载前由集成方设置的全局预配置(<script defer> 之前的内联脚本)。
 * 早于 Web Component 挂载,故能影响 resolveHotel 的首次 hotel/info 查询。
 */
function readPreInitConfig(): Partial<ResolvedConfig> {
  if (typeof window === 'undefined') return {};
  try {
    const g = (window as unknown as Record<string, unknown>)[PREINIT_GLOBAL];
    if (g && typeof g === 'object') return g as Partial<ResolvedConfig>;
  } catch {
    // 访问 window 异常时静默退化
  }
  return {};
}

let currentConfig: ResolvedConfig = {
  basePath: deriveBasePath(),
  customerService: true,
  analytics: true,
  debug: false,
  ...readPreInitConfig(),
};

/**
 * 从当前脚本 URL 推导 basePath。
 * hotel-hub.js 由 <script src="/hub/hotel-hub.js"> 引入,去掉结尾 /hotel-hub.js 即得 basePath('/hub')。
 * currentScript 在模块脚本下为 null,用 querySelector 兜底。
 */
function deriveBasePath(): string {
  if (typeof document === 'undefined') return '';
  let src: string | undefined;
  try {
    const cur = document.currentScript as unknown as { src?: string } | null;
    if (cur?.src) {
      src = cur.src;
    } else {
      const node = document.querySelector(
        'script[src*="/hotel-hub.js"]'
      ) as HTMLScriptElement | null;
      if (node?.src) src = node.src;
    }
  } catch {
    // 访问 document 异常时静默退化
  }
  if (!src) return '';
  try {
    const u = new URL(src, location.href);
    return u.pathname.replace(/\/hotel-hub\.js$/, '') || '';
  } catch {
    return '';
  }
}

/** 设置全局配置(浅合并) */
export function setConfig(opts: SDKConfig): void {
  currentConfig = { ...currentConfig, ...opts };
}

/** 读取当前配置 */
export function getConfig(): ResolvedConfig {
  return currentConfig;
}
